import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, Gift, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { uploadFile } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export const Logo = ({ className = "" }) => (
  <Link to="/" className={`flex items-center gap-2 font-black tracking-tight text-brand-green ${className}`} data-testid="logo-link">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-brand-green text-white">
      <Sparkles className="h-4 w-4" strokeWidth={2.2} />
    </span>
    <span className="text-xl">ReferLinc</span>
  </Link>
);

export const TierBadge = ({ tier }) => (
  <span
    data-testid="payer-tier-badge"
    className="inline-flex items-center rounded-full bg-brand-gold/20 px-3 py-1 text-sm font-bold tracking-wide text-brand-gold-dark dark:text-brand-gold"
    title="Payer tier — relative generosity"
  >
    {tier || "$"}
  </span>
);

export const DistanceChip = ({ miles }) =>
  miles === null || miles === undefined ? null : (
    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground" data-testid="distance-chip">
      <MapPin className="h-3.5 w-3.5" /> {Number(miles).toFixed(1)} mi
    </span>
  );

export const RecencyChip = ({ recency }) =>
  !recency ? null : (
    <span className="inline-flex items-center gap-1 text-sm text-muted-foreground" data-testid="recency-chip">
      <Gift className="h-3.5 w-3.5" /> last payout {recency} ago
    </span>
  );

export const StarRating = ({ value, onChange, size = 28, readOnly = false }) => (
  <div className="flex items-center gap-1" data-testid="star-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={readOnly}
        onClick={() => onChange && onChange(n)}
        data-testid={`star-${n}`}
        className="transition-transform hover:scale-110 disabled:hover:scale-100"
      >
        <Star
          style={{ width: size, height: size }}
          className={n <= value ? "fill-brand-gold text-brand-gold" : "text-muted-foreground/40"}
        />
      </button>
    ))}
  </div>
);

export const RewardOffer = ({ reward, referrerName }) => {
  if (!reward) return null;
  const label =
    reward.reward_type === "cash"
      ? `${referrerName ? referrerName + " earns" : "Reward"} $${(reward.reward_amount_cents / 100).toFixed(0)} for referring you`
      : reward.reward_type === "discount"
      ? `${reward.reward_discount_pct}% off your visit`
      : "A thank-you reward";
  return (
    <div className="bg-gradient-to-br from-brand-green via-brand-green-dark to-[#0e3f30] rounded-[14px] p-6 text-white shadow-[0_20px_40px_rgba(27,122,94,0.25)]" data-testid="reward-banner">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold">Referral reward</p>
      <p className="mt-2 text-2xl font-black leading-tight">{label}</p>
    </div>
  );
};

export const PublicHeader = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <Logo />
        <nav className="flex items-center gap-2">
          <Link to="/business" className="hidden px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground sm:block" data-testid="nav-business">
            For Business
          </Link>
          <Link to="/admin/login" className="hidden px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground sm:block" data-testid="nav-admin">
            Admin
          </Link>
          {user ? (
            <>
              <Button variant="ghost" onClick={() => nav("/app")} data-testid="nav-open-app">My Referrals</Button>
              <Button variant="outline" onClick={logout} data-testid="nav-logout">Log out</Button>
            </>
          ) : (
            <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => nav("/login")} data-testid="nav-login">
              Start referring
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};

export const PublicFooter = () => (
  <footer className="mt-auto border-t border-border bg-secondary/40" data-testid="public-footer">
    <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} ReferLinc · Genixs, Inc.</p>
      <nav className="flex flex-wrap gap-4 text-sm">
        <Link to="/terms" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="footer-terms-link">
          Terms of Service
        </Link>
        <Link to="/privacy" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="footer-privacy-link">
          Privacy Policy
        </Link>
      </nav>
    </div>
  </footer>
);

/** Checkbox + links for signup / business onboarding legal acceptance. */
export const LegalAgreeCheckbox = ({ checked, onCheckedChange, id = "legal-agree" }) => (
  <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-muted-foreground" data-testid="legal-agree-label">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(v) => onCheckedChange(!!v)}
      className="mt-0.5 border-brand-green data-[state=checked]:bg-brand-green"
      data-testid="legal-agree-checkbox"
    />
    <span>
      I agree to the{" "}
      <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-green underline underline-offset-2" data-testid="legal-terms-link">
        Terms of Service
      </Link>{" "}
      and{" "}
      <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-brand-green underline underline-offset-2" data-testid="legal-privacy-link">
        Privacy Policy
      </Link>
    </span>
  </label>
);

export const Loading = ({ label = "Loading…" }) => (
  <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="loading">
    <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-green border-t-transparent" />
    <span className="ml-3">{label}</span>
  </div>
);


export const ImageUploader = ({ onUploaded, label = "Upload image", testid = "image-uploader", uploader }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const url = await (uploader || uploadFile)(file);
      onUploaded(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-dashed border-brand-green/50 bg-brand-green/5 px-4 py-2 text-sm font-medium text-brand-green hover:bg-brand-green/10" data-testid={testid}>
      <Upload className="h-4 w-4" /> {busy ? "Uploading…" : label}
      <input type="file" accept="image/*" className="hidden" onChange={handle} disabled={busy} />
    </label>
  );
};
